import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PROVIDER_LABEL = {
  shopify: 'Shopify',
  square: 'Square',
  stripe: 'Stripe Connect',
};

const ReconnectBanner = () => {
  const [needsReauth, setNeedsReauth] = useState([]);
  const [probing, setProbing] = useState(false);
  const [reconnecting, setReconnecting] = useState('');
  const [error, setError] = useState('');

  const getAuth = () => ({ 'Authorization': `Bearer ${localStorage.getItem('advecti_token')}` });

  const refresh = useCallback(async (signal) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/integrations/status`, { headers: getAuth(), signal });
      if (!res.ok) throw new Error('Status unavailable');
      const data = await res.json();
      const flagged = [];
      Object.entries(data.connections || {}).forEach(([provider, c]) => {
        if (c?.status === 'needs_reauth') {
          flagged.push({ provider, error_reason: c.error_reason || 'token revoked' });
        }
      });
      if (!signal?.aborted) { setNeedsReauth(flagged); setError(''); }
    } catch {
      if (!signal?.aborted) setError('Could not check integration connections. Please try again.');
    }
  }, []);

  const probe = async (signal) => {
    setProbing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/integrations/probe`, { method: 'POST', headers: getAuth(), signal });
      if (!response.ok) throw new Error('Connection check unavailable');
      await refresh(signal);
    } catch {
      if (!signal?.aborted) setError('Could not check integration connections. Please try again.');
    } finally { if (!signal?.aborted) setProbing(false); }
  };

  useEffect(() => {
    const controller = new AbortController();
    refresh(controller.signal);
    // Light-touch probe once when the dashboard first opens — actively detects
    // tokens that were revoked between nightly sync runs.
    probe(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const startReauth = async (provider) => {
    setReconnecting(provider);
    setError('');
    try {
      const path = provider === 'shopify'
        ? `/api/integrations/shopify/start`
        : `/api/integrations/${provider}/start`;
      const opts = provider === 'shopify'
        ? { method: 'POST', headers: { ...getAuth(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ shop: prompt('Your Shopify shop domain (e.g. mystore.myshopify.com):') || '' }) }
        : { headers: getAuth() };
      const res = await fetch(`${BACKEND_URL}${path}`, opts);
      const data = await res.json();
      if (!res.ok || !data.auth_url) throw new Error('Connection unavailable');
      window.location.href = data.auth_url;
    } catch { setError('Could not open the connection page. Please try again.'); }
    finally { setReconnecting(''); }
  };

  if (needsReauth.length === 0 && !error) return null;

  return (
    <div className="bg-amber-50 border border-amber-300 rounded-2xl p-5 mb-6" data-testid="reconnect-banner">
      <div className="flex items-start gap-4 flex-wrap">
        <AlertTriangle className="text-amber-700 mt-0.5 shrink-0" size={22} />
        <div className="flex-1 min-w-0">
          {needsReauth.length > 0 && <><h3 className="text-amber-900 font-bold text-base" data-testid="reconnect-banner-title">
            {needsReauth.length === 1
              ? `${PROVIDER_LABEL[needsReauth[0].provider] || needsReauth[0].provider} needs to be reconnected.`
              : `${needsReauth.length} integrations need to be reconnected.`}
          </h3>
          <p className="text-amber-800/90 text-sm mt-1 break-words" data-testid="reconnect-banner-details">
            We tried to refresh your authorization and the provider returned an error
            ({needsReauth.map(n => n.error_reason).join(', ')}).
            That usually means the token was revoked, scopes changed, or the account hasn't
            been used recently. Click reconnect below to re-authorize.
          </p></>}
          {error && <p role="alert" className="text-amber-900 text-sm break-words" data-testid="reconnect-banner-error">{error}</p>}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {needsReauth.map((n) => (
              <button
                key={n.provider}
                onClick={() => startReauth(n.provider)}
                disabled={reconnecting === n.provider}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
                  reconnecting === n.provider
                    ? 'bg-amber-200 text-amber-700 cursor-not-allowed'
                    : 'bg-amber-700 text-white hover:bg-amber-800'
                }`}
                data-testid={`reconnect-btn-${n.provider}`}
              >
                {reconnecting === n.provider ? 'Redirecting…' : `Reconnect ${PROVIDER_LABEL[n.provider] || n.provider}`}
                <ExternalLink size={14} />
              </button>
            ))}
            <button
              onClick={() => probe()}
              disabled={probing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-amber-800 text-xs hover:underline"
              data-testid="reconnect-probe-btn"
            >
              <RefreshCw size={13} className={probing ? 'animate-spin' : ''} />
              {probing ? 'Re-checking…' : 'Check again'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReconnectBanner;
